import { FC, useState } from 'react'
import { CloseIcon } from '@svgs'
import { useTranslation } from 'react-i18next'

interface ISectionHint {
  // localStorage key so each hint is dismissed independently and stays dismissed
  storageKey: string
  text: string
}

// Dismissible inline hint that explains a section to first-time users —
// the client asked for short in-place instructions because the difference
// between requests, responses and listings confused people (roadmap 16.7).
const SectionHint: FC<ISectionHint> = ({ storageKey, text }) => {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(`hint-dismissed:${storageKey}`) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(`hint-dismissed:${storageKey}`, '1')
    } catch {
      // storage unavailable — hide for this session only
    }
  }

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-brand-soft text-ink text-sm leading-relaxed">
      <span
        aria-hidden
        className="mt-0.5 shrink-0 inline-flex w-5 h-5 items-center justify-center rounded-full bg-brand-strong text-white text-sm font-bold"
      >
        i
      </span>
      <p className="flex-1">{text}</p>
      <button
        onClick={handleDismiss}
        className="icon-button fill-ink hover:fill-brand shrink-0 -m-2 p-2 w-9 h-9 [&_svg]:w-3.5 [&_svg]:h-3.5 inline-flex items-center justify-center"
        aria-label={t('Notifications.dismiss')}
      >
        <CloseIcon />
      </button>
    </div>
  )
}

export default SectionHint
